import React, { useEffect, useState } from "react";
import { VendorTaglineControl } from "../../components/VendorTaglineField";
import { useHistory } from "react-router-dom";
import {
  Card,
  Form,
  Button,
  Spinner,
  Tabs,
  Tab,
  Alert,
} from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { toast } from "react-toastify";
import { getTokenFromLocalStorage } from "utilities/tokenUtils";
import { getUserFromLocalStorage } from "utilities/user.js";
import { useGoogleMaps } from "../../GoogleMapsContext";
import LocationAutocomplete from "../LocationAutoComplete";
import AlertSoundPrefs from "../../components/AlertSoundPrefs";
import VendorAlertSettings from "../../components/VendorAlertSettings";
import VendorTaxSettings from "../../components/VendorTaxSettings";
import OpenHoursManager from "../../components/OpenHours/OpenHoursManager";
import { apiUpdateVendorOpenHours, apiFetchBrandSections } from "../../api/index";
import StoreOfferCard from "../../components/StoreOfferCard";
import usePlacesAutocomplete from "use-places-autocomplete";
import { useGate } from "../../components/team/Gated";
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

const emptyField = (order = 0) => ({
  fieldKey: "",
  label: "",
  fieldType: "text",
  required: false,
  placeholder: "",
  options: "",
  helpText: "",
  order,
});

const WikiSection = ({ icon, title, children }) => (
  <div className="mb-4">
    <div className="d-flex align-items-center gap-2 mb-2 pb-1" style={{ borderBottom: "2px solid #e2e8f0" }}>
      <i className={icon} style={{ fontSize: "0.95rem", color: "#0a578b", width: 18, textAlign: "center" }}></i>
      <span style={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a2f4e" }}>{title}</span>
    </div>
    <div>{children}</div>
  </div>
);

const WikiItem = ({ label, children }) => (
  <div className="mb-2 ps-2" style={{ borderLeft: "3px solid #bfdbfe" }}>
    <div className="small fw-bold" style={{ color: "#0a578b", marginBottom: 2 }}>{label}</div>
    <div className="small text-muted" style={{ lineHeight: 1.6 }}>{children}</div>
  </div>
);

function usePickupPlacesAutocomplete(mapsApiLoaded) {
  const options = {
    requestOptions: {
      componentRestrictions: { country: "ug" },
      debounce: 100,
      location: { lat: () => 0.347596, lng: () => 32.58252 },
      radius: 200 * 1000,
      origin: { lat: 0.3148136, lng: 32.5788721 },
      types: ["establishment"],
    },
    debounce: 100,
  };
  const { value, ready, suggestions: { data, loading: placesLoading, status: suggestionStatus } = { data: [] }, setValue, clearSuggestions } =
    usePlacesAutocomplete(options);
  useEffect(() => { if (mapsApiLoaded) setValue(""); }, [mapsApiLoaded]);
  return { value, ready, data, placesLoading, suggestionStatus, setValue, clearSuggestions };
}

const BrandOwnerSettings = () => {
  const history = useHistory();
  const token = getTokenFromLocalStorage();
  const user = getUserFromLocalStorage();
  const userId = user?.result?._id;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [resolving, setResolving] = useState(true);
  const [brandId, setBrandId] = useState(null);
  // Held separately from formData: hours save on their own endpoint, not through
  // this page's single PUT, so they must not ride along in buildPayload().
  const [brandHours, setBrandHours] = useState(null);
  // Store-wide offer (vendor discounts) and the sections that scope it.
  const [brandStoreOffer, setBrandStoreOffer] = useState(null);
  const [brandSections, setBrandSections] = useState([]);
  const [savingOffer, setSavingOffer] = useState(false);
  const [brandTimezone, setBrandTimezone] = useState("Africa/Kampala");
  const [saving, setSaving] = useState(false);
  // Vendor team permissions: saving the vendor profile is settings:edit.
  const settingsGate = useGate("settings:edit");
  const [savedFormData, setSavedFormData] = useState(null);

  const { mapsApiLoaded } = useGoogleMaps();
  const { value: addrValue, ready: addrReady, data: addrData, placesLoading, suggestionStatus, setValue: setAddrValue, clearSuggestions: clearAddrSuggestions } =
    usePickupPlacesAutocomplete(mapsApiLoaded);

  const [formData, setFormData] = useState({
    tradingName: "",
    description: "",
    phone: "",
    email: "",
    tagline: "",
    address: "",
    latitude: "",
    longitude: "",
    primaryColor: "#324D54",
    secondaryColor: "#c9a227",
    flows: {
      standard: true,
      brandedPackaging: false,
      customQuote: false,
      hybrid: false,
      customQuoteConfig: { introText: "", fields: [] },
    },
    fulfilment: {
      deliveryCoverage: "KAMPALA",
      leadTimeStandardDays: 2,
      leadTimeCustomDays: 14,
      minimumOrderValueUgx: 0,
      returnsPolicyText: "",
    },
    dashboardPrefs: {
      notifyEmailNewOrder: true,
      notifySmsNewOrder: false,
      notifyEmailArtwork: true,
      autoDeclineWhenOos: false,
    },
  });

  useEffect(() => {
    if (!userId || !token) return;
    axios
      .get(`${API_URL}/api/brands`, {
        params: { vendorId: userId },
        headers: authHeaders,
      })
      .then((res) => {
        const list = res.data?.data || res.data || [];
        const brand = Array.isArray(list) ? list[0] : null;
        if (!brand) {
          toast.error("No brand found for your account.");
          return;
        }
        setBrandId(brand._id);
        setBrandHours(brand.openHours || null);
        setBrandTimezone(brand.timezone || "Africa/Kampala");
        setBrandStoreOffer(brand.storeOffer || null);
        const b = brand;
        const initialFormData = {
          tradingName: b.tradingName || b.name || "",
          description: b.description || "",
          phone: b.phone || "",
          email: b.email || "",
          tagline: b.tagline || "",
          address: b.address || "",
          latitude: b.latitude != null ? String(b.latitude) : "",
          longitude: b.longitude != null ? String(b.longitude) : "",
          primaryColor: b.primaryColor || "#324D54",
          secondaryColor: b.secondaryColor || "#c9a227",
          flows: {
            standard: b.flows?.standard !== false,
            brandedPackaging: !!b.flows?.brandedPackaging,
            customQuote: !!b.flows?.customQuote,
            hybrid: !!b.flows?.hybrid,
            customQuoteConfig: {
              introText: b.flows?.customQuoteConfig?.introText || "",
              fields: (b.flows?.customQuoteConfig?.fields || []).map((f) => ({
                fieldKey: f.fieldKey || "",
                label: f.label || "",
                fieldType: f.fieldType || "text",
                required: !!f.required,
                placeholder: f.placeholder || "",
                options: (f.options || []).join(", "),
                helpText: f.helpText || "",
                order: f.order ?? 0,
              })),
            },
          },
          fulfilment: {
            deliveryCoverage: b.fulfilment?.deliveryCoverage || "KAMPALA",
            leadTimeStandardDays: b.fulfilment?.leadTimeStandardDays ?? 2,
            leadTimeCustomDays: b.fulfilment?.leadTimeCustomDays ?? 14,
            minimumOrderValueUgx: b.fulfilment?.minimumOrderValueUgx ?? 0,
            returnsPolicyText: b.fulfilment?.returnsPolicyText || "",
          },
          dashboardPrefs: {
            notifyEmailNewOrder: b.dashboardPrefs?.notifyEmailNewOrder !== false,
            notifySmsNewOrder: !!b.dashboardPrefs?.notifySmsNewOrder,
            notifyEmailArtwork: b.dashboardPrefs?.notifyEmailArtwork !== false,
            autoDeclineWhenOos: !!b.dashboardPrefs?.autoDeclineWhenOos,
          },
        };
        setFormData(initialFormData);
        setSavedFormData(initialFormData);
      })
      .catch(() => toast.error("Failed to load brand settings."))
      .finally(() => setResolving(false));
  }, [userId]);

  const setFlows = (updater) =>
    setFormData((p) => ({ ...p, flows: updater(p.flows) }));

  const setCQC = (updater) =>
    setFlows((f) => ({ ...f, customQuoteConfig: updater(f.customQuoteConfig) }));

  const setField = (idx, key, val) =>
    setCQC((cqc) => {
      const fields = [...cqc.fields];
      fields[idx] = { ...fields[idx], [key]: val };
      return { ...cqc, fields };
    });

  const addField = () =>
    setCQC((cqc) => ({
      ...cqc,
      fields: [...cqc.fields, emptyField(cqc.fields.length)],
    }));

  const removeField = (idx) =>
    setCQC((cqc) => ({
      ...cqc,
      fields: cqc.fields.filter((_, i) => i !== idx),
    }));

  const buildPayload = () => {
    const rawFields = formData.flows.customQuoteConfig.fields || [];
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    return {
      ...formData,
      ...(formData.address  ? { address: formData.address }  : {}),
      ...(!isNaN(lat)       ? { latitude: lat }               : {}),
      ...(!isNaN(lng)       ? { longitude: lng }              : {}),
      fulfilment: {
        ...formData.fulfilment,
        leadTimeStandardDays: Number(formData.fulfilment.leadTimeStandardDays) || 0,
        leadTimeCustomDays: Number(formData.fulfilment.leadTimeCustomDays) || 0,
        minimumOrderValueUgx: Number(formData.fulfilment.minimumOrderValueUgx) || 0,
      },
      flows: {
        ...formData.flows,
        customQuoteConfig: {
          introText: formData.flows.customQuoteConfig.introText || "",
          fields: rawFields
            .filter((f) => f.label && f.label.trim())
            .map((f, i) => ({
              fieldKey:
                f.fieldKey ||
                f.label
                  .toLowerCase()
                  .replace(/\s+/g, "_")
                  .replace(/[^a-z0-9_]/g, ""),
              label: f.label.trim(),
              fieldType: f.fieldType || "text",
              required: !!f.required,
              placeholder: f.placeholder || "",
              options:
                typeof f.options === "string"
                  ? f.options
                      .split(",")
                      .map((o) => o.trim())
                      .filter(Boolean)
                  : f.options || [],
              helpText: f.helpText || "",
              order: i,
            })),
        },
      },
    };
  };

  // Hours have their own endpoint and their own save button. Deliberately NOT
  // folded into this page's single PUT: a vendor closing for the day should not
  // have to submit an unrelated dirty profile form to make it take effect.
  const handleOpenHoursSave = async (openHours, timezone) => {
    if (!brandId) return;
    try {
      await apiUpdateVendorOpenHours("brand", brandId, {
        openHours,
        ...(timezone ? { timezone } : {}),
      });
      setBrandHours(openHours);
      toast.success("Business hours updated");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Could not update business hours"
      );
    }
  };

  useEffect(() => {
    let alive = true;
    if (!brandId) return undefined;
    apiFetchBrandSections(brandId)
      .then((res) => {
        const rows = res?.data?.data || res?.data || [];
        if (alive) setBrandSections(Array.isArray(rows) ? rows : []);
      })
      .catch(() => { if (alive) setBrandSections([]); });
    return () => { alive = false; };
  }, [brandId]);

  const handleStoreOfferSave = async (storeOffer) => {
    if (!brandId) return;
    setSavingOffer(true);
    try {
      const res = await axios.put(`${API_URL}/api/brands/${brandId}`, { storeOffer }, { headers: authHeaders });
      const saved = res?.data?.data?.storeOffer || res?.data?.storeOffer || storeOffer;
      setBrandStoreOffer(saved);
      toast.success(storeOffer.enabled ? "Store-wide offer saved." : "Store-wide offer ended.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save the offer.");
    } finally {
      setSavingOffer(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!brandId) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/brands/${brandId}`, buildPayload(), {
        headers: authHeaders,
      });
      setSavedFormData(JSON.parse(JSON.stringify(formData)));
      toast.success("Brand settings saved.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (resolving) {
    return (
      <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const handlePickupLocationSelect = ({ address, latitude, longitude }) => {
    setFormData((p) => ({ ...p, address, latitude: String(latitude), longitude: String(longitude) }));
    setAddrValue(address, false);
    clearAddrSuggestions();
  };

  const isDirty = !savedFormData || JSON.stringify(formData) !== JSON.stringify(savedFormData);

  const { flows, fulfilment, dashboardPrefs } = formData;
  const cqc = flows.customQuoteConfig;

  return (
    <div className="content">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <Card.Title as="h4" className="mb-0">Brand Settings</Card.Title>
            <p className="card-category mb-0" style={{ fontSize: "0.85rem", color: "#888" }}>
              Manage your brand profile, order flows, and quote form configuration
            </p>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => history.push("/admin/brand-product-management")}
            style={{ whiteSpace: "nowrap" }}
          >
            View Catalog
          </Button>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSave}>
            <Tabs defaultActiveKey="profile" className="mb-4">

              {/* ── Profile ───────────────────────────────────────────── */}
              <Tab eventKey="profile" title="Profile">
                <div className="mt-3">

                  {/* Identity */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-id-badge" style={{ color: "#0a578b", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Brand Identity</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                            <i className="fas fa-store" style={{ color: "#0a578b", fontSize: "0.75rem" }}></i> Trading Name
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            placeholder="e.g. Prime Concepts Ltd"
                            value={formData.tradingName}
                            onChange={(e) => setFormData((p) => ({ ...p, tradingName: e.target.value }))}
                          />
                        </div>
                        <div className="col-md-6">
                          <VendorTaglineControl
                            size="sm"
                            value={formData.tagline}
                            onChange={(e) => setFormData((p) => ({ ...p, tagline: e.target.value }))}
                            labelClassName="small fw-bold d-flex align-items-center gap-1"
                            labelIcon={<i className="fas fa-quote-left" style={{ color: "#0a578b", fontSize: "0.75rem" }}></i>}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-address-book" style={{ color: "#057a55", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Contact Details</span>
                      <span className="text-muted ms-1" style={{ fontSize: "0.72rem", fontWeight: 400 }}>Used for buyer-facing enquiries and notification delivery</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                            <i className="fas fa-phone" style={{ color: "#057a55", fontSize: "0.75rem" }}></i> Contact Phone
                          </Form.Label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: "0.8rem", color: "#94a3b8", pointerEvents: "none" }}>
                              <i className="fas fa-phone-alt"></i>
                            </span>
                            <Form.Control
                              size="sm"
                              placeholder="+256700000000"
                              value={formData.phone}
                              onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                              style={{ paddingLeft: 30 }}
                            />
                          </div>
                          <Form.Text className="text-muted">E.164 format recommended</Form.Text>
                        </div>
                        <div className="col-md-6">
                          <Form.Label className="small fw-bold d-flex align-items-center gap-1">
                            <i className="fas fa-envelope" style={{ color: "#057a55", fontSize: "0.75rem" }}></i> Contact Email
                          </Form.Label>
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: "0.8rem", color: "#94a3b8", pointerEvents: "none" }}>
                              <i className="fas fa-at"></i>
                            </span>
                            <Form.Control
                              size="sm"
                              type="email"
                              placeholder="orders@yourbrand.com"
                              value={formData.email}
                              onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                              style={{ paddingLeft: 30 }}
                            />
                          </div>
                          <Form.Text className="text-muted">Quote confirmation emails are sent from this address</Form.Text>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-align-left" style={{ color: "#7e3af2", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Brand Description</span>
                      <span className="text-muted ms-1" style={{ fontSize: "0.72rem", fontWeight: 400 }}>Rendered on your public storefront</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <p className="small text-muted mb-2">
                        Use <strong>bold</strong> for key services and bullet lists for product ranges. Avoid raw HTML — the editor handles formatting.
                      </p>
                      <ReactQuill
                        theme="snow"
                        value={formData.description}
                        onChange={(val) => setFormData((p) => ({ ...p, description: val }))}
                        modules={{
                          toolbar: [
                            [{ header: [2, 3, false] }],
                            ["bold", "italic", "underline"],
                            [{ list: "ordered" }, { list: "bullet" }],
                            ["link"],
                            ["clean"],
                          ],
                        }}
                        style={{ background: "#fff", borderRadius: "0 0 6px 6px" }}
                      />
                    </div>
                  </div>

                  {/* Brand colours */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-palette" style={{ color: "#b45309", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Brand Colours</span>
                      <span className="text-muted ms-1" style={{ fontSize: "0.72rem", fontWeight: 400 }}>Applied to buttons and headings on your storefront</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <div className="row g-4">
                        <div className="col-md-5">
                          <Form.Label className="small fw-bold mb-2">Primary Colour</Form.Label>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ position: "relative" }}>
                              <Form.Control
                                type="color"
                                value={formData.primaryColor}
                                onChange={(e) => setFormData((p) => ({ ...p, primaryColor: e.target.value }))}
                                style={{ width: 48, height: 48, padding: 3, borderRadius: 10, cursor: "pointer", border: "2px solid #e2e8f0" }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <Form.Control
                                size="sm"
                                value={formData.primaryColor}
                                onChange={(e) => setFormData((p) => ({ ...p, primaryColor: e.target.value }))}
                                style={{ fontFamily: "monospace", fontWeight: 700 }}
                              />
                              <div style={{
                                marginTop: 6, height: 8, borderRadius: 4,
                                background: formData.primaryColor,
                                border: "1px solid rgba(0,0,0,0.08)",
                              }} />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-5">
                          <Form.Label className="small fw-bold mb-2">Secondary Colour</Form.Label>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Form.Control
                              type="color"
                              value={formData.secondaryColor}
                              onChange={(e) => setFormData((p) => ({ ...p, secondaryColor: e.target.value }))}
                              style={{ width: 48, height: 48, padding: 3, borderRadius: 10, cursor: "pointer", border: "2px solid #e2e8f0" }}
                            />
                            <div style={{ flex: 1 }}>
                              <Form.Control
                                size="sm"
                                value={formData.secondaryColor}
                                onChange={(e) => setFormData((p) => ({ ...p, secondaryColor: e.target.value }))}
                                style={{ fontFamily: "monospace", fontWeight: 700 }}
                              />
                              <div style={{
                                marginTop: 6, height: 8, borderRadius: 4,
                                background: formData.secondaryColor,
                                border: "1px solid rgba(0,0,0,0.08)",
                              }} />
                            </div>
                          </div>
                        </div>
                        <div className="col-md-2 d-flex align-items-center">
                          <div style={{
                            borderRadius: 10, overflow: "hidden", width: "100%",
                            border: "1px solid #e2e8f0", fontSize: "0.7rem",
                          }}>
                            <div style={{ background: formData.primaryColor, padding: "8px 10px", color: "#fff", fontWeight: 700 }}>Primary</div>
                            <div style={{ background: formData.secondaryColor, padding: "8px 10px", color: "#fff", fontWeight: 700 }}>Secondary</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Pickup / Warehouse Address ─────────────────────── */}
                  <div style={{ border: !formData.latitude ? "2px solid #f59e0b" : "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginTop: 16 }}>
                    <div style={{ background: !formData.latitude ? "#fffbeb" : "#f8fafc", borderBottom: !formData.latitude ? "2px solid #f59e0b" : "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-map-marker-alt" style={{ color: !formData.latitude ? "#d97706" : "#dc2626", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Pickup / Warehouse Address</span>
                      <span className="ms-1" style={{ fontSize: "0.72rem", fontWeight: 400, color: !formData.latitude ? "#92400e" : "#6b7280" }}>Used as the origin on delivery route maps</span>
                      {!formData.latitude && (
                        <span style={{ marginLeft: "auto", background: "#f59e0b", color: "#fff", fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>⚠ Not set</span>
                      )}
                    </div>
                    <div style={{ padding: 16 }}>
                      {!formData.latitude && (
                        <Alert variant="warning" className="py-2 px-3 mb-3" style={{ fontSize: "0.82rem", borderLeft: "4px solid #f59e0b" }}>
                          <strong>📍 Action required:</strong> Your pickup address is not set. Riders won't have a starting point on delivery route maps. Search and select your warehouse or dispatch location below.
                        </Alert>
                      )}
                      <Form.Label className="small fw-bold d-flex align-items-center gap-1 mb-1">
                        <i className="fas fa-building" style={{ color: "#dc2626", fontSize: "0.75rem" }}></i> Warehouse / Dispatch Address
                      </Form.Label>
                      <LocationAutocomplete
                        value={addrValue}
                        setValue={setAddrValue}
                        ready={addrReady}
                        data={addrData}
                        clearSuggestions={clearAddrSuggestions}
                        mapsApiLoaded={mapsApiLoaded}
                        loadingSuggestions={placesLoading} suggestionStatus={suggestionStatus}
                        defaultValue={formData.address}
                        onLocationSelect={handlePickupLocationSelect}
                        onClear={() => setFormData((p) => ({ ...p, address: "", latitude: "", longitude: "" }))}
                      />
                      {formData.latitude && formData.longitude && (
                        <div className="mt-2 d-flex gap-3">
                          <div style={{ flex: 1 }}>
                            <Form.Label className="small fw-bold mb-0">Latitude</Form.Label>
                            <Form.Control size="sm" readOnly value={formData.latitude} style={{ background: "#f8fafc", color: "#475569" }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <Form.Label className="small fw-bold mb-0">Longitude</Form.Label>
                            <Form.Control size="sm" readOnly value={formData.longitude} style={{ background: "#f8fafc", color: "#475569" }} />
                          </div>
                        </div>
                      )}
                      {formData.latitude && (
                        <Form.Text className="text-success d-block mt-1" style={{ fontSize: "0.78rem" }}>
                          ✓ Location pinned — coordinates will be saved with your profile.
                        </Form.Text>
                      )}
                    </div>
                  </div>

                </div>
              </Tab>

              {/* ── Order Flows ───────────────────────────────────────── */}
              <Tab eventKey="flows" title="Order Flows">
                <div className="mt-3">

                  {/* Flow toggle cards */}
                  <p className="text-muted small mb-3">
                    Enable the buying journeys your brand supports. Multiple flows can be active simultaneously.
                  </p>
                  <div className="row g-3 mb-4">
                    {[
                      {
                        key: "standard",
                        icon: "fas fa-shopping-cart",
                        label: "Standard Catalogue",
                        desc: "Buyers browse fixed-price SKUs and add to cart directly.",
                        color: "#0a578b",
                      },
                      {
                        key: "brandedPackaging",
                        icon: "fas fa-box",
                        label: "Branded Packaging",
                        desc: "Buyers upload print-ready artwork at checkout for custom packaging.",
                        color: "#7e3af2",
                      },
                      {
                        key: "customQuote",
                        icon: "fas fa-file-invoice",
                        label: "Custom Quote",
                        desc: "Buyers submit a quote request form; you respond with a formal quote.",
                        color: "#057a55",
                      },
                      {
                        key: "hybrid",
                        icon: "fas fa-code-branch",
                        label: "Hybrid",
                        desc: "Both catalogue and custom-quote flows active at the same time.",
                        color: "#b45309",
                      },
                    ].map(({ key, icon, label, desc, color }) => {
                      const active = !!flows[key];
                      return (
                        <div className="col-md-6" key={key}>
                          <div
                            onClick={() => setFlows((f) => ({ ...f, [key]: !f[key] }))}
                            style={{
                              border: `2px solid ${active ? color : "#e2e8f0"}`,
                              borderRadius: 10,
                              padding: "14px 16px",
                              cursor: "pointer",
                              background: active ? `${color}0d` : "#fff",
                              transition: "all 0.15s",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 14,
                              userSelect: "none",
                            }}
                          >
                            <div style={{
                              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                              background: active ? color : "#f1f5f9",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <i className={icon} style={{ fontSize: "0.95rem", color: active ? "#fff" : "#94a3b8" }}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                                <span style={{ fontWeight: 700, fontSize: "0.875rem", color: active ? color : "#334155" }}>{label}</span>
                                <span style={{
                                  fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px",
                                  padding: "2px 8px", borderRadius: 20,
                                  background: active ? color : "#e2e8f0",
                                  color: active ? "#fff" : "#64748b",
                                }}>
                                  {active ? "ON" : "OFF"}
                                </span>
                              </div>
                              <p style={{ fontSize: "0.78rem", color: "#64748b", margin: 0, lineHeight: 1.5 }}>{desc}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Catalog shortcut */}
                  {(flows.standard || flows.brandedPackaging || flows.customQuote || flows.hybrid) && (
                    <div style={{
                      background: "#f0f7ff", border: "1px solid #bfdbfe", borderRadius: 8,
                      padding: "10px 16px", display: "flex", alignItems: "center",
                      justifyContent: "space-between", marginBottom: 24,
                    }}>
                      <span style={{ fontSize: "0.82rem", color: "#1e3a5f" }}>
                        <i className="fas fa-box-open me-2" style={{ color: "#0a578b" }}></i>
                        Manage the products shown in your buyer-facing catalogue
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => history.push("/admin/brand-product-management")}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        Open Catalog <i className="fas fa-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  )}

                  {/* Custom Quote Form Config */}
                  {flows.customQuote && (
                    <div style={{ border: "1px solid #d1fae5", borderRadius: 10, background: "#f0fdf4", padding: "20px 20px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                        <i className="fas fa-sliders-h" style={{ color: "#057a55", fontSize: "0.9rem" }}></i>
                        <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "#064e3b" }}>Custom Quote Form Config</span>
                      </div>
                      <p className="text-muted small mb-3">
                        Define the fields buyers must fill in when requesting a quote from your brand page. These appear on the buyer's quote request form.
                      </p>

                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Intro message shown to buyer</Form.Label>
                        <Form.Control
                          as="textarea"
                          size="sm"
                          rows={2}
                          maxLength={400}
                          placeholder="e.g. Please provide artwork details and quantity so we can prepare an accurate quote."
                          value={cqc.introText}
                          onChange={(e) => setCQC((c) => ({ ...c, introText: e.target.value }))}
                          style={{ borderColor: "#a7f3d0" }}
                        />
                        <Form.Text className="text-muted">{cqc.introText.length}/400</Form.Text>
                      </Form.Group>

                      {cqc.fields.length === 0 && (
                        <div style={{
                          border: "1px dashed #6ee7b7", borderRadius: 8, padding: "16px",
                          textAlign: "center", background: "#fff", marginBottom: 12,
                        }}>
                          <i className="fas fa-plus-circle" style={{ color: "#6ee7b7", fontSize: "1.5rem", display: "block", marginBottom: 6 }}></i>
                          <span className="small text-muted">No custom fields yet. Click <strong>+ Add field</strong> to define what buyers must provide.</span>
                        </div>
                      )}

                      {cqc.fields.map((field, idx) => (
                        <div
                          key={idx}
                          style={{ background: "#fff", border: "1px solid #d1fae5", borderRadius: 8, padding: "14px 16px", marginBottom: 12 }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#057a55", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                              <i className="fas fa-grip-vertical me-1" style={{ color: "#a7f3d0" }}></i>
                              Field {idx + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeField(idx)}
                              style={{
                                background: "#fee2e2", color: "#991b1b", border: "none",
                                borderRadius: 6, padding: "3px 10px", fontSize: "0.75rem",
                                fontWeight: 600, cursor: "pointer",
                              }}
                            >
                              <i className="fas fa-trash-alt me-1"></i>Remove
                            </button>
                          </div>
                          <div className="row g-2">
                            <div className="col-md-5">
                              <Form.Label className="small mb-1">Label <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                size="sm"
                                placeholder="e.g. Print dimensions"
                                value={field.label}
                                onChange={(e) => setField(idx, "label", e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <Form.Label className="small mb-1">Type</Form.Label>
                              <AppSelect block
                                size="sm"
                                value={field.fieldType}
                                onChange={(e) => setField(idx, "fieldType", e.target.value)}
                              >
                                <option value="text">Text</option>
                                <option value="textarea">Textarea</option>
                                <option value="select">Select (dropdown)</option>
                                <option value="number">Number</option>
                                <option value="date">Date</option>
                                <option value="file">File link</option>
                              </AppSelect>
                            </div>
                            <div className="col-md-4 d-flex align-items-end pb-1">
                              <Form.Check
                                type="switch"
                                label={<span className="small">Required field</span>}
                                checked={!!field.required}
                                onChange={(e) => setField(idx, "required", e.target.checked)}
                              />
                            </div>
                            <div className="col-md-5">
                              <Form.Label className="small mb-1">Placeholder hint</Form.Label>
                              <Form.Control
                                size="sm"
                                placeholder="Text shown inside the input"
                                value={field.placeholder}
                                onChange={(e) => setField(idx, "placeholder", e.target.value)}
                              />
                            </div>
                            {field.fieldType === "select" && (
                              <div className="col-md-7">
                                <Form.Label className="small mb-1">Options <span className="text-muted">(comma-separated)</span></Form.Label>
                                <Form.Control
                                  size="sm"
                                  placeholder="e.g. A4, A5, A3"
                                  value={field.options}
                                  onChange={(e) => setField(idx, "options", e.target.value)}
                                />
                              </div>
                            )}
                            <div className="col-12">
                              <Form.Label className="small mb-1">Help text <span className="text-muted">(shown below label)</span></Form.Label>
                              <Form.Control
                                size="sm"
                                placeholder="e.g. Width × Height in mm"
                                value={field.helpText}
                                onChange={(e) => setField(idx, "helpText", e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addField}
                        style={{
                          background: "#fff", border: "1px dashed #6ee7b7", borderRadius: 7,
                          padding: "7px 18px", fontSize: "0.8rem", fontWeight: 600,
                          color: "#057a55", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                        }}
                      >
                        <i className="fas fa-plus"></i> Add field
                      </button>
                    </div>
                  )}
                </div>
              </Tab>

              {/* ── Fulfilment ────────────────────────────────────────── */}
              <Tab eventKey="fulfilment" title="Fulfilment">
                <div className="mt-3">
                  <p className="text-muted small mb-4">
                    Set your delivery reach, turnaround expectations, and order minimums. These are displayed to buyers on your brand storefront.
                  </p>

                  {/* Delivery coverage */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-map-marker-alt" style={{ color: "#0a578b", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Delivery Coverage</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <p className="small text-muted mb-3">
                        Shown on your storefront to set buyer expectations. Does not automatically restrict checkout geography.
                      </p>
                      {[
                        { value: "KAMPALA",         icon: "fas fa-city",        label: "Kampala",              desc: "Kampala city deliveries only." },
                        { value: "KAMPALA_CENTRAL",  icon: "fas fa-road",        label: "Kampala + Central",    desc: "Kampala and greater Central region." },
                        { value: "NATIONAL",         icon: "fas fa-flag",        label: "National",             desc: "Deliveries across Uganda." },
                        { value: "EX_WORKS",         icon: "fas fa-warehouse",   label: "Ex-Works only",        desc: "Buyer arranges own collection from your premises." },
                      ].map(({ value, icon, label, desc }) => {
                        const active = fulfilment.deliveryCoverage === value;
                        return (
                          <div
                            key={value}
                            onClick={() => setFormData((p) => ({ ...p, fulfilment: { ...p.fulfilment, deliveryCoverage: value } }))}
                            style={{
                              display: "flex", alignItems: "center", gap: 12,
                              padding: "10px 14px", borderRadius: 8, marginBottom: 8, cursor: "pointer",
                              border: `2px solid ${active ? "#0a578b" : "#e2e8f0"}`,
                              background: active ? "#eff6ff" : "#fff",
                              transition: "all 0.12s",
                              userSelect: "none",
                            }}
                          >
                            <div style={{
                              width: 32, height: 32, borderRadius: 7, flexShrink: 0,
                              background: active ? "#0a578b" : "#f1f5f9",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <i className={icon} style={{ fontSize: "0.8rem", color: active ? "#fff" : "#94a3b8" }}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: "0.82rem", color: active ? "#0a578b" : "#334155" }}>{label}</div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{desc}</div>
                            </div>
                            <div style={{
                              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                              border: `2px solid ${active ? "#0a578b" : "#cbd5e1"}`,
                              background: active ? "#0a578b" : "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              {active && <i className="fas fa-check" style={{ fontSize: "0.55rem", color: "#fff" }}></i>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Lead times + MOV */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-clock" style={{ color: "#7e3af2", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Lead Times &amp; Minimum Order</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <div style={{ background: "#faf5ff", border: "1px solid #ede9fe", borderRadius: 8, padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                              <i className="fas fa-box" style={{ color: "#7e3af2", fontSize: "0.75rem" }}></i>
                              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#5b21b6", textTransform: "uppercase", letterSpacing: "0.6px" }}>Standard Orders</span>
                            </div>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              value={fulfilment.leadTimeStandardDays}
                              onChange={(e) => setFormData((p) => ({ ...p, fulfilment: { ...p.fulfilment, leadTimeStandardDays: e.target.value } }))}
                              style={{ borderColor: "#ede9fe", textAlign: "center", fontWeight: 700, fontSize: "1.1rem" }}
                            />
                            <div style={{ fontSize: "0.72rem", color: "#7e3af2", textAlign: "center", marginTop: 4 }}>working days</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div style={{ background: "#fdf4ff", border: "1px solid #f0abfc", borderRadius: 8, padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                              <i className="fas fa-file-invoice" style={{ color: "#a21caf", fontSize: "0.75rem" }}></i>
                              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#86198f", textTransform: "uppercase", letterSpacing: "0.6px" }}>Custom / Quote Orders</span>
                            </div>
                            <Form.Control
                              size="sm"
                              type="number"
                              min={0}
                              value={fulfilment.leadTimeCustomDays}
                              onChange={(e) => setFormData((p) => ({ ...p, fulfilment: { ...p.fulfilment, leadTimeCustomDays: e.target.value } }))}
                              style={{ borderColor: "#f0abfc", textAlign: "center", fontWeight: 700, fontSize: "1.1rem" }}
                            />
                            <div style={{ fontSize: "0.72rem", color: "#a21caf", textAlign: "center", marginTop: 4 }}>working days</div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                              <i className="fas fa-coins" style={{ color: "#c2410c", fontSize: "0.75rem" }}></i>
                              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#c2410c", textTransform: "uppercase", letterSpacing: "0.6px" }}>Minimum Order</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#9a3412" }}>UGX</span>
                              <Form.Control
                                size="sm"
                                type="number"
                                min={0}
                                value={fulfilment.minimumOrderValueUgx}
                                onChange={(e) => setFormData((p) => ({ ...p, fulfilment: { ...p.fulfilment, minimumOrderValueUgx: e.target.value } }))}
                                style={{ borderColor: "#fed7aa", fontWeight: 700, fontSize: "0.95rem" }}
                              />
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "#c2410c", marginTop: 4 }}>orders below this are refused</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Returns policy */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-undo-alt" style={{ color: "#057a55", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Returns Policy</span>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <p className="small text-muted mb-2">Plain text displayed on your storefront's policy section.</p>
                      <Form.Control
                        as="textarea"
                        size="sm"
                        rows={4}
                        placeholder="e.g. Stock items are returnable within 7 days in original packaging. Custom print orders are non-returnable once production is approved."
                        value={fulfilment.returnsPolicyText}
                        onChange={(e) => setFormData((p) => ({ ...p, fulfilment: { ...p.fulfilment, returnsPolicyText: e.target.value } }))}
                        style={{ borderColor: "#a7f3d0", resize: "vertical" }}
                      />
                    </div>
                  </div>

                </div>
              </Tab>

              {/* ── Business Hours ────────────────────────────────────── */}
              <Tab eventKey="hours" title="Business Hours">
                <div className="mt-3">
                  <p className="text-muted small mb-4">
                    Until you save hours, customers can order from your brand at
                    any time. Once saved, express orders follow these hours.
                    Customers can still order ahead while you are closed unless
                    you switch off scheduled orders below.
                  </p>
                  {/* The editor's save button is type="button" (MUI default), so
                      it does not submit the surrounding profile <Form>. */}
                  <OpenHoursManager
                    vendorLabel="brand"
                    // Brands trade 24/7 until hours are saved.
                    tradingPolicy="open_until_set"
                    vendorType="brand"
                    vendorId={brandId}
                    openHours={brandHours}
                    timezone={brandTimezone}
                    onSave={handleOpenHoursSave}
                    onValidationError={(msg) => toast.error(msg)}
                    isLoading={!brandId}
                  />
                </div>
              </Tab>

              {/* ── Offers (vendor discounts) ────────────────────────── */}
              <Tab eventKey="offers" title="Offers">
                <div className="mt-3">
                  <StoreOfferCard
                    value={brandStoreOffer}
                    sections={brandSections}
                    saving={savingOffer}
                    onSave={handleStoreOfferSave}
                  />
                </div>
              </Tab>

              {/* ── Tax & Receipts ────────────────────────────────────
                  Its own tab, beside Notifications rather than inside the
                  profile form: this is a decision a brand is asked to make
                  about a URA obligation, not another field on an address. */}
              <Tab eventKey="tax" title="Tax &amp; Receipts">
                <div className="mt-3">
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px" }}>
                    <VendorTaxSettings />
                  </div>
                </div>
              </Tab>

              {/* ── Notifications ─────────────────────────────────────── */}
              <Tab eventKey="notifications" title="Notifications">
                <div className="mt-3">
                  <p className="text-muted small mb-4">
                    Configure how you are alerted about activity on your brand. Preferences are saved to your brand profile and respected by the platform's notification dispatcher.
                  </p>

                  {/* In-app alert sound (personal, per-operator) */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                    <AlertSoundPrefs />
                  </div>

                  {/* Order alerts: email / SMS / WhatsApp, contacts, test alert.
                      Shared VendorAlertSettings panel backed by /api/vendor-alerts.
                      The legacy notifyEmailNewOrder / notifySmsNewOrder prefs still
                      ride along in buildPayload() untouched; only their switches
                      moved into the panel. */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                    <VendorAlertSettings />
                  </div>

                  {/* Artwork notifications */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-paint-brush" style={{ color: "#7e3af2", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Artwork &amp; Packaging Alerts</span>
                    </div>
                    <div style={{ padding: "8px 0" }}>
                      {(() => {
                        const key = "notifyEmailArtwork";
                        const on = !!dashboardPrefs[key];
                        return (
                          <div style={{
                            display: "flex", alignItems: "center", gap: 14,
                            padding: "12px 16px",
                            background: on ? "#faf5ff" : "#fff",
                            transition: "background 0.12s",
                          }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                              background: on ? "#7e3af2" : "#f1f5f9",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <i className="fas fa-file-image" style={{ fontSize: "0.85rem", color: on ? "#fff" : "#94a3b8" }}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                                <span style={{ fontWeight: 700, fontSize: "0.83rem", color: "#1e293b" }}>Email me on artwork submissions</span>
                                <span style={{
                                  fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                                  padding: "1px 7px", borderRadius: 20,
                                  background: "#7e3af218", color: "#7e3af2", letterSpacing: "0.6px",
                                }}>Email</span>
                                <span style={{
                                  fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                                  padding: "1px 7px", borderRadius: 20,
                                  background: "#b4530918", color: "#b45309", letterSpacing: "0.6px",
                                }}>Branded Packaging flow only</span>
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                Triggers when a buyer uploads print-ready artwork during checkout.
                              </div>
                            </div>
                            <Form.Check
                              type="switch"
                              checked={on}
                              onChange={(e) => setFormData((p) => ({ ...p, dashboardPrefs: { ...p.dashboardPrefs, [key]: e.target.checked } }))}
                              style={{ flexShrink: 0 }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Automation */}
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                    <div style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <i className="fas fa-robot" style={{ color: "#c2410c", fontSize: "0.85rem", width: 16, textAlign: "center" }}></i>
                      <span style={{ fontWeight: 700, fontSize: "0.82rem", color: "#1a2f4e", textTransform: "uppercase", letterSpacing: "0.6px" }}>Automation</span>
                    </div>
                    <div style={{ padding: "8px 0" }}>
                      {(() => {
                        const key = "autoDeclineWhenOos";
                        const on = !!dashboardPrefs[key];
                        return (
                          <div style={{
                            display: "flex", alignItems: "center", gap: 14,
                            padding: "12px 16px",
                            background: on ? "#fff7ed" : "#fff",
                            transition: "background 0.12s",
                          }}>
                            <div style={{
                              width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                              background: on ? "#c2410c" : "#f1f5f9",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <i className="fas fa-ban" style={{ fontSize: "0.85rem", color: on ? "#fff" : "#94a3b8" }}></i>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                                <span style={{ fontWeight: 700, fontSize: "0.83rem", color: "#1e293b" }}>Auto-decline orders when out of stock</span>
                                {on && (
                                  <span style={{
                                    fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
                                    padding: "1px 7px", borderRadius: 20,
                                    background: "#fee2e2", color: "#991b1b", letterSpacing: "0.6px",
                                  }}>Active</span>
                                )}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                Preference stored against your brand profile. Enforcement depends on stock tracking being active in your catalog.
                              </div>
                            </div>
                            <Form.Check
                              type="switch"
                              checked={on}
                              onChange={(e) => setFormData((p) => ({ ...p, dashboardPrefs: { ...p.dashboardPrefs, [key]: e.target.checked } }))}
                              style={{ flexShrink: 0 }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Status note */}
                  <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <i className="fas fa-info-circle" style={{ color: "#b45309", marginTop: 2, flexShrink: 0 }}></i>
                    <span style={{ fontSize: "0.78rem", color: "#78350f", lineHeight: 1.6 }}>
                      <strong>Delivery status:</strong> Email preferences use the platform's configured SMTP / SendGrid transport (set in server <code>.env</code>). SMS delivery requires a valid E.164 phone number in your Profile tab. All preferences are saved to your brand document and respected by the notification dispatcher.
                    </span>
                  </div>

                </div>
              </Tab>

              {/* ── Guide / Wiki ──────────────────────────────────────── */}
              <Tab eventKey="guide" title="Guide">
                <div className="mt-3" style={{ maxWidth: 780 }}>

                  <WikiSection icon="fas fa-id-card" title="Profile Tab">
                    <WikiItem label="Trading name">
                      The public-facing name shown on your brand page and in order confirmations. Keep it consistent with your physical branding.
                    </WikiItem>
                    <WikiItem label="Tagline">
                      A short one-liner (≤ 120 chars) displayed under your brand name. E.g. <em>"Retail cartons, custom print, and artwork review."</em>
                    </WikiItem>
                    <WikiItem label="Description">
                      Rich-text block rendered on your public storefront. Use <strong>bold</strong> for key services and bullet lists for product ranges.
                      Avoid raw HTML — the editor handles formatting for you.
                    </WikiItem>
                    <WikiItem label="Brand colours">
                      Primary colour is used for buttons and headings on your storefront. Secondary is used for accents.
                      Changes take effect after saving and a page refresh on the buyer side.
                    </WikiItem>
                  </WikiSection>

                  <WikiSection icon="fas fa-code-branch" title="Order Flows Tab">
                    <WikiItem label="Standard catalogue">
                      Buyers browse and add products to cart directly from your catalog. Enable this if you sell fixed-price SKUs.
                      Manage products via <strong>Shop → Catalog</strong>.
                    </WikiItem>
                    <WikiItem label="Branded packaging">
                      Activates an artwork-upload step at checkout. Buyers submit print-ready files.
                      You receive an artwork notification email (configure in Notifications tab).
                    </WikiItem>
                    <WikiItem label="Custom quote">
                      Hides the direct buy button; buyers submit a quote request form instead.
                      Define what fields they must fill in using the <strong>Custom Quote Form Config</strong> panel that appears below when this flow is enabled.
                      Respond to requests under <strong>Shop → Quotes</strong>.
                    </WikiItem>
                    <WikiItem label="Hybrid">
                      Both catalogue and custom-quote flows are active simultaneously.
                      Some SKUs can be ordered directly; others require a quote.
                    </WikiItem>
                    <WikiItem label="Custom Quote Form Config — fields">
                      Each field you add appears on the buyer's quote request form.
                      <ul className="mt-1 mb-0 ps-3" style={{ fontSize: "0.82rem" }}>
                        <li><strong>Label</strong> — shown above the input (required).</li>
                        <li><strong>Type</strong> — text, textarea, number, date, select dropdown, or file link.</li>
                        <li><strong>Required</strong> — buyer cannot submit without filling this in.</li>
                        <li><strong>Options</strong> — comma-separated values for dropdown fields (e.g. <em>A4, A5, A3</em>).</li>
                        <li><strong>Help text</strong> — small hint shown beneath the field.</li>
                      </ul>
                    </WikiItem>
                  </WikiSection>

                  <WikiSection icon="fas fa-truck" title="Fulfilment Tab">
                    <WikiItem label="Delivery coverage">
                      Sets buyer expectation on your storefront. It does not restrict where buyers can order from; contact Lusuku support to enforce that.
                    </WikiItem>
                    <WikiItem label="Lead times">
                      <em>Standard</em> applies to catalogue orders; <em>Custom</em> applies to quote-based orders.
                      Both are displayed on your brand page as estimated turnaround.
                    </WikiItem>
                    <WikiItem label="Minimum order value">
                      Buyers see this minimum, and an order from your brand's cart below it is refused at checkout. Goods only, before the delivery fee. It can raise Lusuku's own minimum order value but never lower it.
                    </WikiItem>
                    <WikiItem label="Returns policy">
                      Plain-text only. Displayed on your storefront's policy section and in order confirmation emails.
                    </WikiItem>
                  </WikiSection>

                  <WikiSection icon="fas fa-bell" title="Notifications Tab">
                    <WikiItem label="Email me on new orders">
                      Preference stored against your brand profile. Email delivery uses the platform's configured SMTP / SendGrid transport.
                    </WikiItem>
                    <WikiItem label="SMS me on new orders">
                      Preference stored against your brand profile. Requires a valid phone number in E.164 format (e.g. <em>+256700000000</em>) in your Profile tab.
                    </WikiItem>
                    <WikiItem label="Email me on artwork submissions">
                      Triggers when a buyer uploads artwork during checkout under the Branded Packaging flow.
                    </WikiItem>
                    <WikiItem label="Auto-decline when out of stock">
                      Preference stored against your brand profile. Automatic enforcement depends on stock tracking being active in your catalog.
                    </WikiItem>
                  </WikiSection>

                  <WikiSection icon="fas fa-box-open" title="Catalog (Shop → Catalog)">
                    <p className="small text-muted mb-2">
                      Your catalog is organised into <strong>sections</strong> (product categories) each containing one or more <strong>products</strong>.
                    </p>
                    <WikiItem label="Creating a section">
                      Click <strong>+ New Section</strong> on the Catalog page. Give it a clear name (e.g. <em>Carton Boxes, Sticker Rolls</em>).
                    </WikiItem>
                    <WikiItem label="Adding a product">
                      Click <strong>Add product</strong> on any section row. Fill in name, price (UGX), stock count, and upload a product image.
                      Image dimensions: <strong>800 × 800 px minimum, 1:1 ratio recommended</strong> for clean display on the buyer storefront.
                    </WikiItem>
                    <WikiItem label="Editing / disabling a product">
                      Click the edit icon on the product card. Toggle <strong>Enabled</strong> off to hide it from buyers without deleting it.
                    </WikiItem>
                  </WikiSection>

                  <WikiSection icon="fas fa-file-invoice" title="Quotes (Shop → Orders & Quotes)">
                    <WikiItem label="Viewing quote requests">
                      All buyer quote requests appear in the <strong>Quote Requests</strong> table. Status <em>OPEN</em> means awaiting your response.
                    </WikiItem>
                    <WikiItem label="Sending a quote">
                      Click <strong>Send quote</strong> on an OPEN request. The quote form captures:
                      <ul className="mt-1 mb-0 ps-3" style={{ fontSize: "0.82rem" }}>
                        <li><strong>Line items</strong> — description, quantity, and unit price per line.</li>
                        <li><strong>Shipping / Freight</strong> — added as a separate line item on the invoice.</li>
                        <li><strong>VAT %</strong> — calculated on the subtotal and shown as a line item.</li>
                        <li><strong>Payment terms</strong> — e.g. 100% upfront, 50% deposit, Net 30.</li>
                        <li><strong>Lead time</strong> — estimated turnaround shown to the buyer.</li>
                        <li><strong>Valid until</strong> — expiry date for the quote.</li>
                        <li><strong>Currency</strong> — UGX or USD.</li>
                        <li><strong>Notes to buyer</strong> — any additional terms or instructions.</li>
                      </ul>
                      On submission the request is closed, the buyer is notified via <strong>Socket.IO in real time</strong> and receives a <strong>full quote email</strong> with the line-item breakdown and an accept link.
                    </WikiItem>
                    <WikiItem label="Accepted quotes">
                      When a buyer accepts, the quote status changes to <em>ACCEPTED</em> and a platform order is created automatically with <code>isBrandOrder: true</code>.
                      Track it under <strong>Shop → Orders &amp; Quotes → Orders tab</strong>.
                    </WikiItem>
                  </WikiSection>

                  <WikiSection icon="fas fa-chart-bar" title="Dashboard">
                    <WikiItem label="Stats cards">
                      Shows four counters scoped to your brand: <strong>New Orders</strong> (processing), <strong>Pending Delivery</strong> (dispatched), <strong>Delivered</strong>, and <strong>Open Quote Requests</strong>. Clicking a card navigates to the relevant table.
                    </WikiItem>
                    <WikiItem label="Real-time updates">
                      Stats and quote counts update live via Socket.IO. A sound notification plays when a new order or quote request arrives (toggle in the Notification Settings card on the dashboard).
                    </WikiItem>
                    <WikiItem label="Brand Shop Analytics">
                      Below the stats cards, a <strong>Brand Shop Analytics</strong> panel shows GA4 data scoped to your brand's shop path on lusuku.ug — including visitors, page views, sessions, bounce rate, average session duration, top pages, and device breakdown. Use the 7d / 14d / 30d selector to change the window.
                    </WikiItem>
                  </WikiSection>

                  <div className="mt-4 p-3 rounded" style={{ background: "#f0f7ff", border: "1px solid #bfdbfe", fontSize: "0.82rem", color: "#1e3a5f" }}>
                    <i className="fas fa-life-ring me-1" style={{ color: "#0a578b" }}></i>
                    <strong>Need help?</strong> Contact the Lusuku platform team at{" "}
                    <a href="mailto:support@lusuku.ug" style={{ color: "#0a578b" }}>support@lusuku.ug</a>.
                  </div>

                </div>
              </Tab>
            </Tabs>

            <div className="d-flex justify-content-end">
              <Button type="submit" variant="primary" disabled={saving || !isDirty || !settingsGate.allowed} title={settingsGate.title} style={{ opacity: (saving || !isDirty || !settingsGate.allowed) ? 0.45 : 1 }}>
                {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving…</> : "Save settings"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default BrandOwnerSettings;
